import React, { useState, useEffect, useRef } from "react";
import {
  Box, Dialog, Typography, Button, CircularProgress,
  IconButton, TextField, Chip, Divider, Slide,
} from "@mui/material";
import {
  MyLocationRounded, Close, Search as SearchIcon,
  LocationOn, CheckCircle, ArrowBack, GpsFixed, Place,
  LocationOff, LocationSearching,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { setPincode, clearPincode } from "../../../redux/slices/orderSlice";

// ─── Google Maps loader (singleton) ───────────────────────────────────────────
const GOOGLE_MAPS_API_KEY = "AIzaSyAjkisEUw7dFaBFmsS3GSC8ivxWnyAYieg";

let _mapsLoaded = false;
let _mapsLoading = false;
const _mapsQueue = [];

function loadGoogleMaps(cb) {
  if (_mapsLoaded && window.google?.maps) { cb(); return; }
  _mapsQueue.push(cb);
  if (_mapsLoading) return;
  _mapsLoading = true;
  const s = document.createElement("script");
  s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
  s.async = true;
  s.onload = () => {
    _mapsLoaded = true;
    _mapsLoading = false;
    _mapsQueue.splice(0).forEach((fn) => fn());
  };
  document.head.appendChild(s);
}

// ─── CSS (injected once) ──────────────────────────────────────────────────────
const ANIM_CSS = `
  @keyframes cl-pulse {
    0%   { transform: scale(1);   opacity: .7; }
    100% { transform: scale(2.6); opacity: 0;  }
  }
  @keyframes cl-bounce {
    0%,100% { transform: translateY(0);    }
    50%      { transform: translateY(-7px); }
  }
  @keyframes cl-shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes cl-fadeup {
    from { opacity:0; transform:translateY(10px); }
    to   { opacity:1; transform:translateY(0);    }
  }
  @keyframes cl-shake {
    0%,100% { transform: translateX(0); }
    20%      { transform: translateX(-6px); }
    40%      { transform: translateX(6px); }
    60%      { transform: translateX(-4px); }
    80%      { transform: translateX(4px); }
  }
  @keyframes cl-spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;
let _cssInjected = false;
function injectCSS() {
  if (_cssInjected) return;
  _cssInjected = true;
  const el = document.createElement("style");
  el.textContent = ANIM_CSS;
  document.head.appendChild(el);
}

const SlideUp = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

function LocationPermissionScreen({ status, onAllow, onSkip }) {
  const isDenied = status === "denied";

  return (
    <Box sx={{
      flex: 1, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      bgcolor: "#f4f8ff", px: 3, py: 4, textAlign: "center",
      animation: "cl-fadeup .3s ease",
    }}>
      {/* Animated location icon */}
      <Box sx={{ position: "relative", mb: 3 }}>
        <Box sx={{
          width: 96, height: 96, borderRadius: "50%",
          bgcolor: isDenied ? "#fff0f0" : "#e8f2ff",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative",
        }}>
          {/* pulse rings — only when not denied */}
          {!isDenied && [0, 1].map((k) => (
            <Box key={k} sx={{
              position: "absolute", inset: -(k + 1) * 14,
              borderRadius: "50%",
              border: `2px solid rgba(0,113,220,${0.25 - k * 0.1})`,
              animation: "cl-pulse 2s ease-out infinite",
              animationDelay: `${k * 0.5}s`,
              opacity: 0,
            }} />
          ))}
          {isDenied
            ? <LocationOff sx={{ fontSize: 44, color: "#e53935" }} />
            : <LocationSearching sx={{
                fontSize: 44, color: "#0071dc",
                animation: "cl-spin 2s linear infinite",
              }} />
          }
        </Box>
      </Box>

      <Typography sx={{
        fontFamily: "'Nunito',sans-serif", fontWeight: 800,
        fontSize: 20, color: "#1a1a2e", mb: 1,
      }}>
        {isDenied ? "Location access blocked" : "Allow location access"}
      </Typography>

      <Typography sx={{
        fontFamily: "'Nunito',sans-serif", fontSize: 14,
        color: "#777", lineHeight: 1.6, mb: 3,
        maxWidth: 320,
      }}>
        {isDenied
          ? "You've blocked location access. To use your current location, please enable it in your browser settings and reload the page."
          : "We need your location to show nearby stores and delivery options for your area."
        }
      </Typography>

      {isDenied ? (
        // How-to guidance when blocked
        <Box sx={{
          bgcolor: "#fff3f3", border: "1.5px solid #ffd0d0",
          borderRadius: 3, p: 2, mb: 3, textAlign: "left", width: "100%", maxWidth: 360,
        }}>
          <Typography sx={{ fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 13, color: "#c62828", mb: 1 }}>
            How to enable location:
          </Typography>
          {[
            "Click the 🔒 lock icon in your browser's address bar",
            'Find "Location" and set it to "Allow"',
            "Reload this page and try again",
          ].map((step, i) => (
            <Box key={i} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 12, color: "#c62828", flexShrink: 0 }}>
                {i + 1}.
              </Typography>
              <Typography sx={{ fontFamily: "'Nunito',sans-serif", fontSize: 12, color: "#555" }}>
                {step}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Button
          onClick={onAllow}
          variant="contained"
          fullWidth
          startIcon={<MyLocationRounded />}
          sx={{
            bgcolor: "#0071dc", color: "#fff",
            fontWeight: 800, fontFamily: "'Nunito',sans-serif",
            fontSize: 15, py: 1.5, borderRadius: 3,
            textTransform: "none", mb: 1.5,
            maxWidth: 360,
            boxShadow: "0 4px 20px rgba(0,113,220,.32)",
            "&:hover": { bgcolor: "#005bb5", transform: "translateY(-1px)" },
            transition: "all .2s",
          }}
        >
          Enable Location Access
        </Button>
      )}

      <Button
        onClick={onSkip}
        variant="text"
        sx={{
          color: "#999", fontFamily: "'Nunito',sans-serif",
          fontWeight: 600, fontSize: 13, textTransform: "none",
          "&:hover": { color: "#555", bgcolor: "transparent" },
        }}
      >
        Search manually instead →
      </Button>
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MapView — fully unmounts/remounts on every dialog open (via key in parent)
// ══════════════════════════════════════════════════════════════════════════════
function MapView({ onLocationChange }) {
  const mapDivRef   = useRef(null);
  const mapRef      = useRef(null);
  const markerRef   = useRef(null);
  const geocoderRef = useRef(null);
  const acRef       = useRef(null);
  const debRef      = useRef(null);

  // 'unknown' → check permission → show prompt or go straight to map
  const [permStatus,       setPermStatus]       = useState("unknown");
  const [tilesLoaded,      setTilesLoaded]      = useState(false);
  const [locating,         setLocating]          = useState(false);
  const [searchVal,        setSearchVal]          = useState("");
  const [suggestions,      setSuggestions]        = useState([]);
  const [showSug,          setShowSug]            = useState(false);
  const [selectedLocation, setSelectedLocation]   = useState(null);
  const [error,            setError]              = useState(null);
  const [showMap,          setShowMap]            = useState(false);
  const dispatch = useDispatch();

  console.log("see the location",selectedLocation);

  // ── on mount: check permission then decide what screen to show ────────────
  useEffect(() => {
    injectCSS();

    // If Permissions API is available, query silently first
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted") {
          // Already granted — skip permission screen, go straight to map
          setPermStatus("granted");
          setShowMap(true);
          loadGoogleMaps(initMap);
        } else if (result.state === "denied") {
          setPermStatus("denied");
        } else {
          // 'prompt' — show our custom permission screen first
          setPermStatus("prompt");
        }

        // Listen for live changes (e.g. user re-enables in settings)
        result.onchange = () => {
          if (result.state === "granted") {
            setPermStatus("granted");
            setShowMap(true);
            loadGoogleMaps(initMap);
          } else if (result.state === "denied") {
            setPermStatus("denied");
          }
        };
      }).catch(() => {
        // Permissions API not supported — just request directly
        setPermStatus("prompt");
      });
    } else {
      // No Permissions API (iOS Safari etc) — show prompt screen
      setPermStatus("prompt");
    }

    return () => {
      if (markerRef.current) markerRef.current.setMap(null);
      clearTimeout(debRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── when showMap flips true, init map (mapDivRef may not exist yet) ────────
  useEffect(() => {
    if (showMap) {
      // Small delay to let React render the map div
      const t = setTimeout(() => loadGoogleMaps(initMap), 80);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  // ── permission screen: user clicked "Allow" ────────────────────────────────
  function handleAllowLocation() {
    setPermStatus("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPermStatus("granted");
        setShowMap(true);
        // detectLocation will be called inside initMap once map is ready
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setPermStatus("denied");
        } else {
          // Other error (timeout, unavailable) — still show map, just no auto-locate
          setPermStatus("granted");
          setShowMap(true);
        }
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }

  // ── skip to manual search ──────────────────────────────────────────────────
  function handleSkip() {
    setPermStatus("granted"); // treat as "proceed without location"
    setShowMap(true);
  }

  // ── init Google Map ────────────────────────────────────────────────────────
  function initMap() {
    if (!mapDivRef.current) return;

    const map = new window.google.maps.Map(mapDivRef.current, {
      center: { lat: 20.5937, lng: 78.9629 },
      zoom: 5,
      disableDefaultUI: true,
      gestureHandling: "greedy",
      styles: [
        { featureType: "poi",           stylers: [{ visibility: "off" }] },
        { featureType: "transit",       stylers: [{ visibility: "off" }] },
        { featureType: "road",          elementType: "geometry", stylers: [{ color: "#ffffff" }] },
        { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
        { featureType: "road.highway",  elementType: "geometry", stylers: [{ color: "#e8e8e8" }] },
        { featureType: "water",         elementType: "geometry", stylers: [{ color: "#c9e8f7" }] },
        { featureType: "landscape",     elementType: "geometry", stylers: [{ color: "#f8f8f8" }] },
      ],
    });

    mapRef.current      = map;
    geocoderRef.current = new window.google.maps.Geocoder();
    acRef.current       = new window.google.maps.places.AutocompleteService();

    map.addListener("tilesloaded", () => setTilesLoaded(true));
    map.addListener("click", (e) => {
      dropPin(e.latLng);
      geocode(e.latLng.lat(), e.latLng.lng());
    });

    // Auto-detect if permission is already granted
    detectLocation(map);
  }

  // ── marker ─────────────────────────────────────────────────────────────────
  function dropPin(latLng) {
    if (markerRef.current) markerRef.current.setMap(null);
    const marker = new window.google.maps.Marker({
      position: latLng,
      map: mapRef.current,
      draggable: true,
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 0 },
    });
    markerRef.current = marker;
    marker.addListener("dragend", () => {
      const p = marker.getPosition();
      geocode(p.lat(), p.lng());
    });
  }

  // ── geolocation ────────────────────────────────────────────────────────────
  function detectLocation(mapInstance) {
    const map = mapInstance || mapRef.current;
    if (!navigator.geolocation || !map) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = new window.google.maps.LatLng(pos.coords.latitude, pos.coords.longitude);
        map.panTo(ll);
        map.setZoom(17);
        dropPin(ll);
        geocode(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          setPermStatus("denied");
          setShowMap(false);
        } else {
          setError("Couldn't detect location. Try searching.");
          setTimeout(() => setError(null), 4000);
        }
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  }

  // ── reverse geocode ────────────────────────────────────────────────────────
  async function geocode(lat, lng) {
    if (!geocoderRef.current) return;
    try {
      const results = await new Promise((res, rej) =>
        geocoderRef.current.geocode({ location: { lat, lng } }, (r, s) =>
          s === "OK" && r[0] ? res(r) : rej()
        )
      );
      const comps = results[0].address_components;
      let pincode = "", city = "", area = "", locality = "";
      for (const c of comps) {
        if (c.types.includes("postal_code"))                 pincode  = c.long_name;
        if (c.types.includes("locality"))                    locality = c.long_name;
        if (c.types.includes("administrative_area_level_2")) city     = c.long_name;
        if (c.types.includes("sublocality_level_1"))         area     = c.long_name;
      }
      const loc = {
        lat, lng,
        address:      results[0].formatted_address,
        shortAddress: area || locality || city,
        pincode,
        city: city || locality,
        locality,
        area,
      };
      setSelectedLocation(loc);
      dispatch(setPincode(loc.pincode));
    } catch {
      setError("Couldn't fetch address. Tap somewhere else.");
      setTimeout(() => setError(null), 3000);
    }
  }

  // ── autocomplete ───────────────────────────────────────────────────────────
  function handleSearch(val) {
    setSearchVal(val);
    clearTimeout(debRef.current);
    if (!val.trim() || !acRef.current) { setSuggestions([]); setShowSug(false); return; }
    debRef.current = setTimeout(() => {
      acRef.current.getPlacePredictions(
        { input: val, componentRestrictions: { country: "in" } },
        (preds, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setSuggestions(preds.slice(0, 5));
            setShowSug(true);
          }
        }
      );
    }, 280);
  }

  function pickSuggestion(pred) {
    setSearchVal(pred.description);
    setSuggestions([]);
    setShowSug(false);
    const svc = new window.google.maps.places.PlacesService(mapRef.current);
    svc.getDetails({ placeId: pred.place_id, fields: ["geometry"] }, (place, status) => {
      if (status === "OK") {
        const loc = place.geometry.location;
        mapRef.current.panTo(loc);
        mapRef.current.setZoom(17);
        dropPin(loc);
        geocode(loc.lat(), loc.lng());
      }
    });
  }

  // ── render: permission screen ──────────────────────────────────────────────
  if (!showMap) {
    return (
      <LocationPermissionScreen
        status={permStatus === "requesting" ? "requesting" : permStatus}
        onAllow={handleAllowLocation}
        onSkip={handleSkip}
      />
    );
  }

  // ── render: map ────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Search bar */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1, bgcolor: "#fff", position: "relative", zIndex: 20, flexShrink: 0 }}>
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1,
          bgcolor: "#f4f6fb", border: "1.5px solid #e0e4ee", borderRadius: 3,
          px: 1.5, py: 0.5,
          transition: "border-color .2s, background .2s",
          "&:focus-within": { borderColor: "#0071dc", bgcolor: "#fff" },
        }}>
          <SearchIcon sx={{ color: "#0071dc", fontSize: 20, flexShrink: 0 }} />
          <TextField
            placeholder="Search area, landmark or street…"
            value={searchVal}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => suggestions.length && setShowSug(true)}
            variant="standard"
            fullWidth
            InputProps={{
              disableUnderline: true,
              sx: {
                fontFamily: "'Nunito',sans-serif", fontSize: 13.5, color: "#1a1a2e",
                py: 0.6, "& input::placeholder": { color: "#aaa" },
              },
              endAdornment: searchVal ? (
                <IconButton size="small" onClick={() => { setSearchVal(""); setShowSug(false); }}>
                  <Close sx={{ fontSize: 15, color: "#bbb" }} />
                </IconButton>
              ) : null,
            }}
          />
        </Box>

        {/* Autocomplete dropdown */}
        {showSug && suggestions.length > 0 && (
          <Box sx={{
            position: "absolute", top: "calc(100% - 2px)", left: 16, right: 16,
            bgcolor: "#fff", borderRadius: "0 0 14px 14px",
            border: "1.5px solid #0071dc", borderTop: "none",
            boxShadow: "0 10px 28px rgba(0,113,220,.13)",
            overflow: "hidden", zIndex: 100,
            animation: "cl-fadeup .15s ease",
          }}>
            {suggestions.map((s, i) => (
              <Box
                key={s.place_id}
                onClick={() => pickSuggestion(s)}
                sx={{
                  display: "flex", alignItems: "flex-start", gap: 1.5,
                  px: 2, py: 1.2, cursor: "pointer",
                  borderBottom: i < suggestions.length - 1 ? "1px solid #f0f2f7" : "none",
                  "&:hover": { bgcolor: "#f4f8ff" }, transition: "background .15s",
                }}
              >
                <LocationOn sx={{ color: "#0071dc", fontSize: 18, mt: 0.3, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", fontFamily: "'Nunito',sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {s.structured_formatting.main_text}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#999", fontFamily: "'Nunito',sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {s.structured_formatting.secondary_text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Map */}
      <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <Box
          ref={mapDivRef}
          sx={{
            width: "100%", height: "100%",
            opacity: tilesLoaded ? 1 : 0,
            transition: "opacity .5s ease",
          }}
        />

        {/* Shimmer skeleton */}
        {!tilesLoaded && (
          <Box sx={{
            position: "absolute", inset: 0,
            background: "linear-gradient(90deg,#e8ecf4 25%,#f0f3fa 50%,#e8ecf4 75%)",
            backgroundSize: "200% 100%",
            animation: "cl-shimmer 1.5s infinite",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <CircularProgress size={30} sx={{ color: "#0071dc" }} />
          </Box>
        )}

        {/* Animated CSS pin — sits at map center */}
        {selectedLocation && tilesLoaded && (
          <Box sx={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none", zIndex: 5,
            display: "flex", flexDirection: "column", alignItems: "center",
          }}>
            <Box sx={{
              animation: "cl-bounce 2.4s ease-in-out infinite",
              display: "flex", flexDirection: "column", alignItems: "center",
            }}>
              <Box sx={{
                width: 40, height: 40,
                background: "linear-gradient(145deg,#1a8cff,#0057b0)",
                borderRadius: "50% 50% 50% 0",
                transform: "rotate(-45deg)",
                boxShadow: "0 6px 20px rgba(0,113,220,.45)",
                display: "flex", alignItems: "center", justifyContent: "center",
                position: "relative",
              }}>
                <Box sx={{ width: 13, height: 13, borderRadius: "50%", bgcolor: "#fff", transform: "rotate(45deg)" }} />
                {[0, 1].map((k) => (
                  <Box key={k} sx={{
                    position: "absolute", inset: -7, borderRadius: "50%",
                    border: "2px solid #0071dc",
                    animation: "cl-pulse 1.8s ease-out infinite",
                    animationDelay: `${k * 0.5}s`, opacity: 0,
                  }} />
                ))}
              </Box>
              <Box sx={{ width: 10, height: 4, borderRadius: "50%", bgcolor: "rgba(0,0,0,.18)", filter: "blur(2px)", mt: 0.4 }} />
            </Box>
          </Box>
        )}

        {/* Zoom controls */}
        <Box sx={{ position: "absolute", top: 12, right: 12, display: "flex", flexDirection: "column", gap: 0.5, zIndex: 10 }}>
          {[{ lbl: "+", d: 1 }, { lbl: "−", d: -1 }].map(({ lbl, d }) => (
            <IconButton
              key={lbl} size="small"
              onClick={() => mapRef.current?.setZoom(mapRef.current.getZoom() + d)}
              sx={{
                width: 34, height: 34, bgcolor: "#fff", borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,.14)",
                fontSize: 18, fontWeight: 800, color: "#444",
                "&:hover": { bgcolor: "#f0f7ff", color: "#0071dc" },
                transition: "all .18s",
              }}
            >{lbl}</IconButton>
          ))}
        </Box>

        {/* GPS button */}
        <IconButton
          onClick={() => detectLocation()}
          disabled={locating}
          sx={{
            position: "absolute", bottom: 14, right: 14, zIndex: 10,
            width: 46, height: 46, bgcolor: "#fff", borderRadius: 2.5,
            boxShadow: "0 4px 16px rgba(0,0,0,.16)", transition: "all .2s",
            "&:hover": { bgcolor: "#0071dc", "& .gps-icon": { color: "#fff" } },
            "&:disabled": { bgcolor: "#f5f5f5" },
          }}
        >
          {locating
            ? <CircularProgress size={20} sx={{ color: "#0071dc" }} />
            : <GpsFixed className="gps-icon" sx={{ fontSize: 22, color: "#0071dc", transition: "color .2s" }} />
          }
        </IconButton>
      </Box>

      {/* Bottom sheet */}
      <Box sx={{
        bgcolor: "#fff", borderRadius: "20px 20px 0 0",
        boxShadow: "0 -4px 20px rgba(0,0,0,.07)",
        px: { xs: 2, md: 3 }, pt: 2, pb: { xs: 3, md: 2.5 },
        position: "relative", zIndex: 10, flexShrink: 0,
      }}>
        {/* drag handle */}
        <Box sx={{ width: 38, height: 4, borderRadius: 2, bgcolor: "#e0e4ee", mx: "auto", mb: 2 }} />

        {/* Use current location row */}
        {/* <Box
          onClick={() => !locating && detectLocation()}
          sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            p: 1.2, borderRadius: 2.5, mb: 1.5,
            cursor: locating ? "default" : "pointer",
            "&:hover": { bgcolor: "#f0f7ff" },
            transition: "background .18s",
          }}
        >
          <Box sx={{
            width: 36, height: 36, borderRadius: 2, bgcolor: "#e8f2ff",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            {locating
              ? <CircularProgress size={18} sx={{ color: "#0071dc" }} />
              : <MyLocationRounded sx={{ fontSize: 19, color: "#0071dc" }} />
            }
          </Box>
          <Typography sx={{
            fontFamily: "'Nunito',sans-serif", fontWeight: 700,
            fontSize: 14, color: locating ? "#aaa" : "#0071dc",
          }}>
            {locating ? "Detecting your location…" : "Use my current location"}
          </Typography>
        </Box> */}

        {/* <Divider sx={{ mb: 1.5, borderColor: "#f0f2f7" }} /> */}

        {/* Address card */}
        {selectedLocation ? (
          <Box sx={{ animation: "cl-fadeup .25s ease" }}>
            <Typography sx={{
              fontSize: 10, fontWeight: 800, color: "#aaa",
              textTransform: "uppercase", letterSpacing: 1,
              mb: 1, fontFamily: "'Nunito',sans-serif",
            }}>
              Delivery location
            </Typography>

            <Box sx={{
              display: "flex", alignItems: "flex-start", gap: 1.5,
              p: 1.5, bgcolor: "#f7faff",
              borderRadius: 2.5, border: "1.5px solid #dde8ff", mb: 2,
            }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 2, bgcolor: "#0071dc",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Place sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontWeight: 800, fontSize: 15, color: "#1a1a2e",
                  fontFamily: "'Nunito',sans-serif", lineHeight: 1.3, mb: 0.4,
                }}>
                  {selectedLocation.shortAddress || selectedLocation.city || "Selected Location"}
                </Typography>
                <Typography sx={{
                  fontSize: 12, color: "#777", fontFamily: "'Nunito',sans-serif",
                  lineHeight: 1.45, display: "-webkit-box",
                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}>
                  {selectedLocation.address}
                </Typography>
                {selectedLocation.pincode && (
                  <Chip
                    label={`PIN ${selectedLocation.pincode}`}
                    size="small"
                    sx={{
                      mt: 0.75, height: 20, fontSize: 10, fontWeight: 700,
                      bgcolor: "#e0edff", color: "#0057b0",
                      fontFamily: "'Nunito',sans-serif",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
              </Box>
            </Box>

            <Button
              fullWidth variant="contained"
              onClick={() => onLocationChange(selectedLocation, true)}
              startIcon={<CheckCircle sx={{ fontSize: 20 }} />}
              sx={{
                bgcolor: "#0071dc", color: "#fff",
                fontWeight: 800, fontFamily: "'Nunito',sans-serif",
                fontSize: 15, py: 1.5, borderRadius: 3, textTransform: "none",
                boxShadow: "0 4px 20px rgba(0,113,220,.32)",
                "&:hover": {
                  bgcolor: "#005bb5",
                  boxShadow: "0 6px 24px rgba(0,113,220,.44)",
                  transform: "translateY(-1px)",
                },
                transition: "all .2s",
              }}
            >
              Confirm Delivery Location
            </Button>
          </Box>
        ) : (
          <Box sx={{ py: 2, textAlign: "center" }}>
            <Typography sx={{
              fontSize: 13, fontFamily: "'Nunito',sans-serif",
              color: locating ? "#0071dc" : "#ccc",
              fontWeight: locating ? 700 : 400,
            }}>
              {locating ? "Detecting your location…" : "Tap the map or search to set your location"}
            </Typography>
          </Box>
        )}

        {error && (
          <Typography sx={{
            fontSize: 12, color: "#e53935", mt: 1,
            textAlign: "center", fontFamily: "'Nunito',sans-serif",
            animation: "cl-shake .4s ease",
          }}>
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}


const CurrentLocation = ({ open, onClose, onLocationSelect }) => {
  const [mountKey, setMountKey] = useState(0);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setMountKey((k) => k + 1);
    }
    wasOpen.current = open;
  }, [open]);

  function handleLocationChange(loc, confirmed) {
    if (confirmed) {
      onLocationSelect(loc);
      onClose();
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      TransitionComponent={SlideUp}
      PaperProps={{
        sx: {
          bgcolor: "#f4f6fb", overflow: "hidden",
          maxWidth: { xs: "100%", md: 860 },
          maxHeight: { xs: "100%", md: "90vh" },
          mx: "auto", my: { md: "5vh" },
          borderRadius: { md: "20px" },
          boxShadow: "0 32px 80px rgba(0,0,0,.22)",
          display: "flex", flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        bgcolor: "#fff",
        px: { xs: 2, md: 3 }, pt: { xs: 1.5, md: 2 }, pb: 1.5,
        borderBottom: "1px solid #eef0f4",
        display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0,
      }}>
        <IconButton
          onClick={onClose} size="small"
          sx={{ color: "#1a1a2e", bgcolor: "#f4f6fb", borderRadius: 2, p: 0.75, "&:hover": { bgcolor: "#e8eaf0" } }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontFamily: "'Nunito',sans-serif", fontWeight: 800,
            fontSize: 17, color: "#1a1a2e", lineHeight: 1.2,
          }}>
            Select Delivery Location
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#aaa", mt: 0.15, fontFamily: "'Nunito',sans-serif" }}>
            Pinpoint your exact delivery address
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#bbb", "&:hover": { color: "#555" } }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* MapView — key forces full remount every open */}
      <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <MapView key={mountKey} onLocationChange={handleLocationChange} />
      </Box>
    </Dialog>
  );
};

export default CurrentLocation;